import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import { getInboundShipmentForInvoice } from "../../../graphql/inbound/custom-queries";
import { syncableInvoiceCreateProps } from "../../../graphql/invoice/invoiceCRUD";

import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Button, useListContext, useNotify, useRedirect } from "react-admin";

import {
  AclType,
  CarrierType,
  InboundStatus,
  InvoicingMode,
  MAX_INVOICE_LINE_ITEMS,
} from "../../../utils/constants";
import {
  isAuthorized,
  isSubscriptionDisabled,
  logError,
} from "../../../utils/appUtils";
import { deepRedirect, formatCurrency } from "../../../utils/utils";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
  sanitizeZapierData,
  transformZapierInvoiceData,
  triggerEvent,
} from "../../../utils/zapierUtils";

import {
  AddAllIcon,
  AddIcon,
  CancelIcon,
  InvoiceIcon,
  OKIcon,
} from "../../../components/common/iconography";

import { ChargeEvent } from "../accounts/chargeRules/chargeRuleConstants";
import {
  computeAggregatedShipmentFields,
  createInvoiceBase,
  createInvoiceLambda,
  createInvoiceServiceLambda,
  createLambdaInvoiceServices,
  createTransform,
  fetchAccountMap,
  fetchReloadYard,
  findOversizedInvoice,
  getTotalInvoiced,
  isInvoiced,
  logInvoiceCreateHistory,
  setupInboundILineItems,
  setupInboundInvoice,
  updateLambdaInbound,
  setInvoiceSyncPending,
  setInvoiceSyncFailed,
} from "../invoices/InvoiceUtils";
import { hasOnPullStorageRule } from "../accounts/chargeRules/ChargeRuleUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

/**
 * Inbound is invoicable when RELEASED, not skipped, and has data to bill:
 * - Quantity: totalQuantity + totalUOM, or inventories with quantity shipped/delivered
 * - Weight: scale weights (tare + laden + weightUnits) or netWeight + weightUnits
 * Billing UOM comes from charge rules.
 */
const isCollected = (record) =>
  Boolean(record?.collectedDateTime || record?.isCollected);

const isInvoicable = (record) => {
  if (!record || record.status !== InboundStatus.RELEASED || record.skipInvoice)
    return false;

  // Do not return false if account has no billing rules
  // users can create Accesorial Charges invoices manually for each shipment
  const billingAccountId = record.billToAccountId || record.accountId;
  if (
    hasOnPullStorageRule(billingAccountId) &&
    record.carrierType === CarrierType.RAIL &&
    !isCollected(record)
  ) {
    return false;
  }

  // removing requirement to satisfy no-inventory SIT cars
  // support shipment-level only billing
  // const hasQuantity =
  //   Boolean(record.totalUOM) &&
  //   record.inventories?.items?.length > 0 &&
  //   record.inventories.items.some(
  //     (i) =>
  //       (i.quantityAdvised ?? i.quantityReceived) != null &&
  //       (i.quantityAdvised ?? i.quantityReceived) !== "",
  //   );
  // const hasWeight =
  //   (record.tareWeight != null &&
  //     record.ladenWeight != null &&
  //     record.weightUnits) ||
  //   (record.netWeight != null && record.weightUnits);
  // return Boolean(hasQuantity || hasWeight);
  return true;
};

const fetchInboundMap = async (selectedIds) => {
  const inboundMap = {};
  let promiseList = [];

  selectedIds.forEach((inboundId) => {
    promiseList.push(
      API.graphql(
        graphqlOperation(getInboundShipmentForInvoice, { id: inboundId }),
      ),
    );
  });
  let results = await Promise.all(promiseList);

  results.forEach((res) => {
    const inbound = res.data?.getInboundShipment;
    if (inbound && isInvoicable(inbound) && !isInvoiced(inbound)) {
      inboundMap[inbound.id] = inbound;
    }
  });

  return inboundMap;
};

export const CreateInvoiceButton = ({ record, backPath, ...props }) => {
  const history = useHistory();
  const isDisabled = isSubscriptionDisabled();

  const handleCreate = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const billingAccountId = record.billToAccountId || record.accountId;
    deepRedirect(history, "/invoices/create", backPath, {
      event: ChargeEvent.INBOUND,
      accountId: billingAccountId,
      inboundShipmentId: record.id,
    });
  };

  const handleOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const invoiceId = record?.invoice?.id || (record?.invoiceId || "").trim();
    if (!invoiceId) return;
    deepRedirect(history, `/invoices/${invoiceId}`, backPath, {});
  };

  if (!record || !isInvoicable(record) || !isAuthorized(AclType.INVOICES))
    return null;
  if (isInvoiced(record)) {
    const total = formatCurrency(getTotalInvoiced(record));
    return (
      <Button label={total} color="primary" onClick={handleOpen}>
        <InvoiceIcon />
      </Button>
    );
  }
  return (
    <Button
      label="Invoice"
      color="primary"
      onClick={handleCreate}
      disabled={isDisabled}
    >
      <AddIcon />
    </Button>
  );
};

/**
 * PER_SHIPMENT mode: one invoice per inbound
 */
function generateInvoicesPerShipment(
  inboundMap,
  accountMap,
  prefix,
  counterStart,
) {
  const invoiceCreateList = [];
  const invoiceServiceCreateMap = {};
  const inboundUpdateMap = {};
  let counter = counterStart;

  Object.values(inboundMap).forEach((inbound) => {
    if (!isInvoicable(inbound) || isInvoiced(inbound)) return;

    counter++;
    // Generate base invoice
    const billingAccountId = inbound.billToAccountId || inbound.accountId;
    const account = accountMap[billingAccountId];

    const baseInvoice = setupInboundInvoice(
      account,
      inbound,
      account.chargeRules,
      account.services,
    );
    const invoiceInput = createInvoiceLambda(baseInvoice);

    // create lineItems to calc totals on transform - needs invoiceId
    const lineItems = createLambdaInvoiceServices(invoiceInput);
    invoiceInput.lineItems = { items: lineItems };

    // transform & sanitize
    const transformedInput = createTransform(invoiceInput, prefix, counter); // TODO: pass isSyncEnabled
    invoiceCreateList.push(transformedInput);
    invoiceServiceCreateMap[invoiceInput.id] = lineItems;
    inboundUpdateMap[invoiceInput.id] = updateLambdaInbound(
      inbound,
      baseInvoice,
    );
  });

  return {
    invoiceCreateList,
    invoiceServiceCreateMap,
    inboundUpdateMap,
    counter,
  };
}

/**
 * BULK mode: one invoice per account, grouped by shipment
 */
function generateInvoicesBulk(inboundMap, accountMap, prefix, counterStart) {
  const invoiceCreateList = [];
  const invoiceServiceCreateMap = {};
  const inboundUpdateMap = {};
  let counter = counterStart;

  Object.entries(accountMap).forEach(([accountId, account]) => {
    const relatedInbounds = Object.values(inboundMap).filter(
      (inbound) =>
        (inbound.billToAccountId || inbound.accountId) === accountId &&
        isInvoicable(inbound) &&
        !isInvoiced(inbound),
    );

    if (relatedInbounds.length === 0) return;

    counter++;
    const baseInvoice = {
      ...createInvoiceBase(account, null, null),
      event: ChargeEvent.INBOUND,
    };
    const invoiceInput = createInvoiceLambda(baseInvoice);
    const invoiceId = invoiceInput.id;

    const invoiceServiceInputs = [];
    const billedInbounds = [];
    relatedInbounds.forEach((inbound) => {
      const lineItems = setupInboundILineItems(
        inbound,
        account.chargeRules,
        account.services,
      );
      // needs invoiceId
      const serviceItems = lineItems.items.map((item, idx) =>
        createInvoiceServiceLambda(invoiceInput, item, idx),
      );

      invoiceServiceInputs.push(...serviceItems);

      if (serviceItems.length > 0) {
        billedInbounds.push(inbound);
        const inboundUpdateInput = updateLambdaInbound(inbound, invoiceInput);

        if (!inboundUpdateMap[invoiceId]) {
          inboundUpdateMap[invoiceId] = [];
        }
        inboundUpdateMap[invoiceId].push(inboundUpdateInput);
      }
    });

    invoiceInput.lineItems = { items: invoiceServiceInputs };

    const shipmentFields = computeAggregatedShipmentFields(billedInbounds);
    invoiceInput.poNumbers = shipmentFields.poNumbers;
    invoiceInput.railCarNumbers = shipmentFields.railCarNumbers;
    invoiceInput.pickupNumbers = shipmentFields.pickupNumbers;

    const transformedInput = createTransform(invoiceInput, prefix, counter); // TODO: pass isSyncEnabled
    invoiceCreateList.push(transformedInput);
    invoiceServiceCreateMap[invoiceId] = invoiceServiceInputs;
  });

  return {
    invoiceCreateList,
    invoiceServiceCreateMap,
    inboundUpdateMap,
    counter,
  };
}

export const BulkCreateInvoiceButton = (props) => {
  const classes = useStyles();
  const notify = useNotify();
  const redirect = useRedirect();
  const { onSelect, selectedIds } = useListContext();

  const [saving, setSaving] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState(InvoicingMode.PER_SHIPMENT);
  const [isSyncEnabled, setIsSyncEnabled] = useState(
    getTriggerEnabled("invoiceCreated"),
  );

  useEffect(() => {
    const checkSyncEnabled = async () => {
      const triggerEnabled = await fetchTriggerEnabled("invoiceCreated");
      setIsSyncEnabled(triggerEnabled);
    };
    checkSyncEnabled();
  }, []);

  const isDisabled = isSubscriptionDisabled();

  const handleCreate = async (mode) => {
    try {
      setOpenDialog(false);
      setSaving(true);
      debugger;

      const inboundMap = await fetchInboundMap(selectedIds);
      const invoicableInbounds = Object.keys(inboundMap).length;

      if (!invoicableInbounds) {
        onSelect([]);
        setSaving(false);
        notify("No eligible shipments to invoice.", "warning");
        return;
      }

      const accountMap = await fetchAccountMap(ChargeEvent.INBOUND, inboundMap);
      const reloadYard = await fetchReloadYard();

      const prefix = reloadYard.invoiceNumberPrefix;
      let counter = reloadYard.invoiceCounter;

      let invoiceCreateList, invoiceServiceCreateMap, inboundUpdateMap;

      if (mode === InvoicingMode.PER_SHIPMENT || invoicableInbounds === 1) {
        ({
          invoiceCreateList,
          invoiceServiceCreateMap,
          inboundUpdateMap,
          counter,
        } = generateInvoicesPerShipment(
          inboundMap,
          accountMap,
          prefix,
          counter,
        ));
      } else {
        ({
          invoiceCreateList,
          invoiceServiceCreateMap,
          inboundUpdateMap,
          counter,
        } = generateInvoicesBulk(inboundMap, accountMap, prefix, counter));
      }

      const oversized = findOversizedInvoice(
        invoiceCreateList,
        invoiceServiceCreateMap,
      );
      if (oversized) {
        setSaving(false);
        notify(
          `Invoice for ${oversized.invoice.accountName} would have ${oversized.lineCount} line items. Max. ${MAX_INVOICE_LINE_ITEMS} per invoice. Reduce your selection.`,
          "warning",
        );
        return; // stop here, don't create
      }

      debugger;
      let reloadYardUpdateInput = null;
      if (counter !== reloadYard.invoiceCounter) {
        reloadYardUpdateInput = {
          id: reloadYard.id,
          active: reloadYard.active,
          name: reloadYard.name,
          invoiceCounter: counter,
        };
      }

      const response = await API.post("transREST", "/invoiceCreateBulk", {
        body: {
          invoiceCreateList: invoiceCreateList,
          invoiceServiceCreateMap: invoiceServiceCreateMap,
          inboundUpdateMap: inboundUpdateMap,
          outboundUpdateMap: null,
          reloadYardUpdateInput: reloadYardUpdateInput,
          user: window.appUser.email,
          reloadYardId: window.appUser.reloadYardId,
        },
      });
      if (response.error) throw response.error;

      // Log history for each created invoice and sync to zapier
      invoiceCreateList.forEach((invoiceData) => {
        try {
          logInvoiceCreateHistory(invoiceData);

          // trigger zapier event
          if (isSyncEnabled) {
            const items = invoiceServiceCreateMap[invoiceData.id] || [];
            const transformedInvoice = transformZapierInvoiceData(
              {
                ...invoiceData,
                lineItems: { items: items },
              },
              accountMap[invoiceData.accountId]?.services,
            );
            const sanitizedInvoice = sanitizeZapierData(
              syncableInvoiceCreateProps,
              transformedInvoice,
            );
            triggerEvent(sanitizedInvoice, "invoiceCreated")
              .then(() => {
                setInvoiceSyncPending(invoiceData);
              })
              .catch((error) => {
                logError(
                  "inboundShipment:BulkCreateInvoiceButton:triggerEvent",
                  error,
                );
                setInvoiceSyncFailed(invoiceData);
              });
          }
        } catch (error) {
          setInvoiceSyncFailed(invoiceData);
          logError(
            "inboundShipment:BulkCreateInvoiceButton:sanitizeZapierData",
            error,
            notify,
          );
        }
      });

      const invoiceNum = invoiceCreateList.length;
      notify(
        `${invoiceNum} Invoice${
          invoiceNum === 1 ? "" : "s"
        } created successfully`,
      );
      redirect("/invoices");
    } catch (err) {
      logError(
        "inboundShipment:BulkCreateInvoiceButton:handleCreate",
        err,
        notify,
        redirect,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDialogOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={saving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Button
        label="Invoice"
        color="default"
        onClick={handleDialogOpen}
        disabled={isDisabled}
      >
        <AddAllIcon />
      </Button>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Select Invoicing Method</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            aria-label="invoice mode"
          >
            <FormControlLabel
              value={InvoicingMode.PER_SHIPMENT}
              control={<Radio />}
              label="Create 1 invoice per shipment"
            />
            <FormControlLabel
              value={InvoicingMode.BULK}
              control={<Radio />}
              label="Create 1 invoice for all shipments"
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" color="default" onClick={handleDialogClose}>
            <CancelIcon />
          </Button>
          <Button
            label="Continue"
            color="primary"
            onClick={() => handleCreate(mode)}
          >
            <OKIcon />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
